/* eslint-disable sonarjs/cognitive-complexity */
/* eslint-disable @typescript-eslint/no-use-before-define */
/* eslint-disable react/no-danger */
/**
 *
 * Component: MultiSelectTree
 * Date: 15/6/2020
 *
 */
import React, { useEffect, useReducer, useRef, useCallback } from 'react';
import Search from 'antd/lib/input/Search';
import dompurify from 'dompurify';
import mixpanel from 'utils/mixpanel';

import _ from 'lodash';

import { Checkbox, Icon, Text, Button } from 'components/common';
import FilterLayout from 'components/FilterLayout';

import styles from './style.css';

const MultiSelectTree = ({
  filter = {},
  selectedValues,
  filterValues,
  tagRules,
  handleClear,
  onFilterApplied,
  moreFilter,
  hideTitle,
  multiSelectFilter,
  setShowMoreFilter,
  buttonName,
  single,
}: any): JSX.Element => {
  const multiSelectFilterRef = useRef<any>();
  const initialState = {
    displayedSelectedValues: [],
    searchText: '',
    displayedFilterValues: [],
    selectedValuesState: selectedValues,
    showFilter: false,
    totalColumns: 0,
    title: '',
  };

  function reducer(state: any, action: any) {
    switch (action.type) {
      case 'filterDisplayedValues': {
        const {
          displayedSelectedValues,
          searchText,
          displayedFilterValues,
          selectedValuesState,
          totalColumns,
        } = action.payload;
        return {
          ...state,
          displayedSelectedValues,
          searchText,
          displayedFilterValues,
          selectedValuesState,
          totalColumns,
        };
      }
      case 'selectFilterValue': {
        const { title, selectedValuesState } = action.payload;
        return {
          ...state,
          title,
          selectedValuesState,
        };
      }
      case 'setCondition': {
        return {
          ...state,
          conditionState: action.payload,
        };
      }
      case 'setRuleTypeState': {
        return {
          ...state,
          ruleTypeState: action.payload,
        };
      }
      case 'setCheckTypeState': {
        return {
          ...state,
          checkTypeState: action.payload,
        };
      }
      case 'setShowFilter': {
        return {
          ...state,
          showFilter: action.payload,
        };
      }
      case 'handleClose': {
        return {
          ...state,
          searchText: '',
          displayedFilterValues: [],
          selectedValuesState: [],
          displayedSelectedValues: [],
          showFilter: false,
          conditionState: state.conditionState || 'in',
        };
      }
      case 'updateSelectedValues': {
        return { ...state, selectedValuesState: action.payload };
      }
      case 'cancelChanges': {
        return {
          ...state,
          selectedValuesState: selectedValues,
        };
      }
      default:
        throw new Error();
    }
  }

  const [state, dispatch] = useReducer(reducer, initialState);

  const {
    displayedFilterValues,
    selectedValuesState,
    showFilter,
    searchText,
    totalColumns,
    title,
  } = state;

  useEffect(() => {
    filterDisplayedValues('', selectedValues);
  }, [selectedValues]);

  /**
   * This function is supposed to order selected values and remaining values in the list in ascending order one after other
   * @param -> {Array} filterArgs and {Array} selectedStateArgs
   * @return -> filtered, Grouped and merger Array.
   *
   */
  const functionForValuesOrder = useCallback(
    (filterArgs, selectedStateArgs) => {
      // TODO: use immer patch
      let newSelectedValues = filterArgs.filter((f: any) =>
        selectedStateArgs.includes(f._id),
      );
      const selectedValuesId = selectedStateArgs;
      const displayFilter = _.orderBy(
        filterArgs.filter((f: any) => {
          if (selectedValuesId.includes(f._id)) {
            return false;
          }
          return true;
        }),
        ['group', 'name'],
        ['asc', 'asc'],
      );

      newSelectedValues = _.orderBy(
        newSelectedValues,
        ['group', 'name'],
        ['asc', 'asc'],
      );

      return [...newSelectedValues, ...displayFilter];
    },
    [],
  );

  /**
   * this function is used to handle display selected values of drop down, which will change count shown and list of options showed
   *
   * @param -> {String} searchTextArgs and {Array} valuesArgs
   * @return -> Reducer action to update displayedSelectedValues, searchText, displayedFilterValues state
   *
   */
  const filterDisplayedValues = useCallback(
    (searchTextArgs, selectedValuesStateArgs) => {
      let newdisplayedFilterValues = [];
      // this function is to set searched text bold in options
      const searchOption = searchTextArgs
        .trimLeft()
        .replace(RegExp('[!?+@#$%^&*_.]', 'g'), (match: any) => `\\${match}`);
      const testString = (s: any, b: any) => {
        if (s) {
          const match = RegExp(`(?:\\b|_)(${b})`, 'i').exec(s); // Test word boundary or starts with '_'
          if (match) return true;
        }
        return false;
      };

      const boldString = (s: any, b: any) => {
        // match[0] is full match whereas [1] is only the string without leading '_'
        const match = RegExp(`(?:\\b|_)(${b})`, 'i').exec(s) || ['', ''];
        return s.replace(RegExp(`${match[1]}`, 'i'), '<b>$&</b>'); // where '$&' returns matched substring
      };

      // console.log(searchTextArgs, filterValues, displayedFilterValues);

      if (searchTextArgs) {
        filterValues.forEach((option: any) => {
          let matchInTagName = testString(option.title, searchOption);
          if (!matchInTagName)
            option.children.some((itm: any) => {
              matchInTagName = testString(itm.title, searchOption);
              return matchInTagName;
            });
          if (matchInTagName) {
            newdisplayedFilterValues.push({
              ...option,
              formattedName: (
                <span
                  dangerouslySetInnerHTML={{
                    __html: dompurify.sanitize(
                      boldString(option.title, searchOption),
                    ),
                  }}
                />
              ),
            });
          }
        });
      } else {
        newdisplayedFilterValues = _.cloneDeep(filterValues);
      }

      let newTotalColumns = 0;
      filterValues.forEach((f: any) => {
        if (f.children) newTotalColumns += f?.children?.length || 0;
      });

      // added formattedName and formattedGroup to keep original values untouched which are used in tooltip etc
      const updatedSelectedValues =
        selectedValuesStateArgs || selectedValuesState;
      const displayedFilterValuesId = newdisplayedFilterValues.map(
        (f: any) => f._id,
      );
      const newdisplayedSelectedValues = updatedSelectedValues.filter(
        (id: any) => displayedFilterValuesId.includes(id),
      );

      newdisplayedFilterValues = _.cloneDeep(
        functionForValuesOrder(
          newdisplayedFilterValues,
          selectedValuesStateArgs || selectedValuesState,
        ),
      );

      const valuesSelected: any[] = [];
      filterValues.forEach((section: any) => {
        if (section.children)
          section.children.forEach(
            (innerChild: any) =>
              selectedValuesStateArgs.includes(innerChild.key) &&
              valuesSelected.push(innerChild.key),
          );
      });

      dispatch({
        payload: {
          displayedSelectedValues: newdisplayedSelectedValues,
          searchText: searchTextArgs,
          displayedFilterValues: _.cloneDeep(newdisplayedFilterValues),
          selectedValuesState:
            valuesSelected || selectedValuesStateArgs || selectedValuesState,
          totalColumns: newTotalColumns,
        },
        type: 'filterDisplayedValues',
      });
    },
    [filterValues, selectedValuesState, functionForValuesOrder],
  );

  /**
   * Called every time an option is selected
   *
   * @param -> {Number} index of option and {Object} e for checked status
   * @return -> Reducer action to update items state
   *
   */
  const selectFilterValue = (id: any, titleArg: any, e: any) => {
    let newSelectedValues = _.cloneDeep(selectedValuesState);
    const selectedAllValues: any[] = [];

    displayedFilterValues.forEach((group: any) => {
      if (group.children)
        group.children.forEach(
          (agent: any) =>
            (agent.key === id || group.key === id) &&
            selectedAllValues.push(agent.key),
        );
    });

    if (!_.isEmpty(selectedAllValues)) {
      newSelectedValues = newSelectedValues.filter(
        (s: any) => !selectedAllValues.includes(s),
      );
    }
    if (e.target.checked) {
      newSelectedValues.push(...selectedAllValues);
    } else {
      newSelectedValues = newSelectedValues.filter((s: any) => s !== id);
    }
    if (single) {
      if (newSelectedValues.find((s: any) => s === id)) {
        newSelectedValues = newSelectedValues.filter((s: any) => s === id);
      } else {
        newSelectedValues.push(id);
      }
    }

    dispatch({
      payload: {
        selectedValuesState: newSelectedValues,
        title: titleArg,
      },
      type: 'selectFilterValue',
    });
  };
  /**
   * Called every time when select all checkbox is clicked
   *
   * @param -> {Object} event for checked status
   * @return -> Reducer action to update items state
   *
   */
  const selectAllFilterValues = (event: any) => {
    const newSelectedValues: any[] = [];
    if (event.target.checked) {
      displayedFilterValues.forEach((section: any) => {
        // newSelectedValues.push(section.key);
        if (section.children)
          section.children.forEach((question: any) =>
            newSelectedValues.push(question.key),
          );
      });
    }
    dispatch({
      payload: {
        selectedValuesState: newSelectedValues,
      },
      type: 'selectFilterValue',
    });
  };

  /**
   * Function to get Button name as it is variable depending on selected values
   *
   * @return -> button name
   *
   */
  const getButtonName = () => {
    let newTitle = buttonName;
    if (selectedValues?.length === 1) {
      newTitle = filterValues.find(
        (template: any) => template.key === selectedValues[0],
      );
      if (!_.isEmpty(newTitle)) {
        ({ title: newTitle } = newTitle);
      }
      if (!newTitle) {
        newTitle = title;
      }
      return `${buttonName} • ${newTitle || selectedValues[0]}`;
    }
    if (selectedValuesState?.length > 1) {
      return `${buttonName} • ${selectedValuesState.length}`;
    }
    return buttonName;
  };

  const resetStateOfPlusButton = () => {
    if (!selectedValues?.length) handleClose();
  };

  const handleClose = () => {
    dispatch({
      type: 'handleClose',
    });
  };

  const isFullyCheckedOption = useCallback(
    (filterValue: any) => {
      const filterItems =
        filterValue?.children?.filter((item: any) => {
          return selectedValuesState.includes(item.key);
        }) || [];
      return (
        filterItems.length > 0 &&
        filterValue?.children?.length &&
        filterItems.length === filterValue.children.length
      );
    },
    [selectedValuesState],
  );

  const isPartiallyChecked = useCallback(
    (filterValue: any) => {
      const filterItems =
        filterValue?.children?.filter((item: any) =>
          selectedValuesState.includes(item.key),
        ) || [];
      return (
        filterItems.length > 0 &&
        filterValue?.children?.length &&
        filterItems.length !== filterValue.children.length
      );
    },
    [selectedValuesState],
  );

  const getFilter = () => (
    <div>
      {filterValues.length ? (
        <div>
          <div>
            <Search
              allowClear
              autoFocus
              placeholder={buttonName || 'Search Columns'}
              value={searchText}
              onChange={(e) => {
                // console.log(e.target.value);
                filterDisplayedValues(e.target.value, selectedValues);
              }}
            />
          </div>
          {!single && (
            <div style={{ marginTop: '.5rem', marginLeft: '.5rem' }}>
              <Checkbox
                className="checkbox"
                checked={
                  selectedValuesState.length > 0 &&
                  selectedValuesState.length === totalColumns
                }
                indeterminate={
                  selectedValuesState.length > 0 &&
                  selectedValuesState.length !== totalColumns
                }
                onChange={selectAllFilterValues}
              />
              <span>
                {` ${selectedValuesState.length} of ${totalColumns} selected`}
              </span>
              <hr className="divider" />
            </div>
          )}
          <div className="multiselectFilterContent">
            {displayedFilterValues.map((filterValue: any) => (
              <section key={filter.key} className="channel-option-sep">
                <Checkbox
                  key={filterValue.key}
                  className="checkbox"
                  checked={isFullyCheckedOption(filterValue)}
                  indeterminate={isPartiallyChecked(filterValue)}
                  onChange={
                    (e: any) =>
                      selectFilterValue(filterValue.key, filterValue.title, e)
                    // eslint-disable-next-line react/jsx-curly-newline
                  }
                >
                  <span style={{ fontWeight: 'bold' }}>
                    {filterValue.title}
                  </span>
                </Checkbox>
                <div className="question-option">
                  {filterValue.children?.map((question: any) => (
                    <div key={question.key}>
                      <Checkbox
                        className="checkbox"
                        checked={selectedValuesState.includes(question.key)}
                        onChange={
                          (e: any) =>
                            selectFilterValue(question.key, question.title, e)
                          // eslint-disable-next-line react/jsx-curly-newline
                        }
                      >
                        <span className="agent-name-checktitle">
                          {question.title}
                        </span>
                      </Checkbox>
                    </div>
                  ))}
                </div>
              </section>
            ))}
          </div>
        </div>
      ) : null}
    </div>
  );
  const handleClickOutside = () => {
    dispatch({ payload: false, type: 'setShowFilter' });
    handleListener(false);
  };

  /**
   * Handles listener apply and removal.
   * */
  const handleListener = (bool: any) => {
    if (bool) {
      document.addEventListener('mousedown', handleClickEvent);
    } else {
      document.removeEventListener('mousedown', handleClickEvent);
    }
  };

  /**
   * Detects if click is done outside of component or not.
   * */
  const handleClickEvent = (e: any) => {
    const path = e.path || (e.composedPath && e.composedPath());
    if (
      !path.includes(multiSelectFilterRef.current) &&
      !e.target.classList.value.includes('ant-select-dropdown-menu-item') &&
      e.target.classList.value !== 'ant-select-selection__rendered' &&
      e.target.classList.value !== 'ant-select-item-option-content' &&
      !e.target.classList.value.includes('ant-select-item')
    ) {
      // added extra conditions in if because of equal and not equal condition select option
      handleClickOutside();
    }
  };

  /**
   * returns the key from filterValues and section.children as a Array
   *  */
  const getOrderedColumns = (value: any) => {
    const top: any[] = [];
    if (filterValues) {
      filterValues.forEach((section: any) => {
        // if (value.includes(section.key)) {
        //   top.push(section.key);
        // }
        if (section.children)
          section.children.forEach((question: any) => {
            if (value.includes(question.key)) {
              top.push(question.key);
            }
          });
      });
    }
    return [...new Set(top)];
  };

  /**
   * Renders FilerLayout on showFilter enabled, else undefined.
   * Wrapping the filter component in Filter Layout for the consistency.
   * */
  const getFilterComponent = () => {
    let component;
    if (showFilter) {
      component = (
        <FilterLayout
          onCancel={() => {
            dispatch({ type: 'cancelChanges' });
            filterDisplayedValues('', selectedValues);
            handleClickOutside();
          }}
          onApply={() => {
            const selectedAgents = getOrderedColumns(selectedValuesState) || [];
            handleClickOutside();
            onFilterApplied(filter.field, selectedAgents);
            resetStateOfPlusButton();

            // Mixpanel filter applied
            mixpanel('Agent Select', 'Insights', {
              userId: `${localStorage.getItem('userEmail')}`,
              selectedAgents,
            });
          }}
        >
          {getFilter()}
        </FilterLayout>
      );
      // }
    }
    return component;
  };

  /**
   * Counts the total length of data present if flattened.
   * */
  const selectedValuesLength = () => {
    let totalLength = selectedValues?.length || 0;
    if (multiSelectFilter) {
      totalLength = 0;
      selectedValues.forEach((selectedArgs: any) => {
        totalLength += selectedArgs?.value?.length || 0;
      });
    } else if (tagRules) {
      return !(selectedValues === undefined || selectedValues.length === 0);
    }
    return totalLength;
  };

  return (
    <div ref={multiSelectFilterRef} className={styles.container}>
      <div className={styles.filterButtonContainer}>
        {moreFilter && !hideTitle && (
          <Text type="subtitle" text={filter?.name} />
        )}
        <div className="grid-column">
          <div className="filter-button">
            <Button
              id={!selectedValuesLength() ? 'plusBtn' : null}
              height="25px"
              iconSize={12}
              useAntd
              type={selectedValuesLength() ? 'chips' : 'chips-secondary'}
              icon={
                !selectedValuesLength() && (moreFilter || tagRules)
                  ? 'plus'
                  : null
              }
              onClick={() => {
                if (multiSelectFilter && selectedValues?.length > 1) {
                  setShowMoreFilter(true);
                } else {
                  dispatch({
                    type: 'setShowFilter',
                    payload: !showFilter,
                  });
                  handleListener(!showFilter);
                }

                // Mixpanel filter clicked
                mixpanel('Agent Dropdown', 'Insights', {
                  userId: `${localStorage.getItem('userEmail')}`,
                });
              }}
              text={
                selectedValuesLength() || !moreFilter ? getButtonName() : ''
              }
            />
          </div>

          {filter.field !== 'tag' &&
            !tagRules &&
            moreFilter &&
            selectedValues?.length > 0 && (
              <Icon
                size={12}
                onClick={handleClear ? () => handleClear(filter.field) : null}
                type="delete"
              />
              // eslint-disable-next-line @typescript-eslint/indent
            )}
        </div>
      </div>
      {getFilterComponent()}
    </div>
  );
};
export default MultiSelectTree;
